import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { X } from 'lucide-react';
import { Device } from '../types';

interface LatestDataModalProps {
  device: Device;
  isOpen: boolean;
  onClose: () => void;
}

interface LatestMessageData {
  timestamp: string;
  data: Record<string, any>;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  justify-content: flex-end;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalPanel = styled.div<{ isOpen: boolean }>`
  background: #1a1a1a;
  width: 500px;
  max-width: 90vw;
  height: 100vh;
  overflow-y: auto;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.5);
  animation: slideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #0a0a0a;
  }

  &::-webkit-scrollbar-thumb {
    background: #404040;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #505050;
  }
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #2a2a2a;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  position: sticky;
  top: 0;
  background: #1a1a1a;
  z-index: 10;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const DeviceName = styled.h2`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
`;

const LastUpdate = styled.div`
  color: #888888;
  font-size: 13px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #888888;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: #ffffff;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  flex: 1;
`;

const DataSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #2a2a2a;

  &:last-child {
    border-bottom: none;
  }
`;

const DataLabel = styled.span`
  color: #888888;
  font-size: 14px;
  flex: 1;
`;

const DataValue = styled.span`
  color: #ffffff;
  font-size: 14px;
  font-weight: 500;
  text-align: right;
`;

const NoDataMessage = styled.div`
  color: #888888;
  font-size: 14px;
  text-align: center;
  padding: 40px 20px;
`;

const LoadingMessage = styled.div`
  color: #888888;
  font-size: 14px;
  text-align: center;
  padding: 40px 20px;
`;

const ModalFooter = styled.div`
  padding: 24px;
  border-top: 1px solid #2a2a2a;
  display: flex;
  justify-content: flex-end;
  position: sticky;
  bottom: 0;
  background: #1a1a1a;
  z-index: 10;
`;

const CancelButton = styled.button`
  background: #2a2a2a;
  color: #ffffff;
  border: none;
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #3a3a3a;
  }
`;

const LatestDataModal: React.FC<LatestDataModalProps> = ({ device, isOpen, onClose }) => {
  const [latestData, setLatestData] = useState<LatestMessageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchLatestData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, device.id]);

  const fetchLatestData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/devices/${device.id}/latest`);

      if (!response.ok) {
        throw new Error('Failed to fetch latest data');
      }

      const data = await response.json();
      setLatestData(data);
    } catch (error) {
      console.error('Error fetching latest data:', error);
      setLatestData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `Last updated ${diffMins} minute${diffMins > 1 ? 's' : ''} ago.`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Last updated ${diffHours} hour${diffHours > 1 ? 's' : ''} ago.`;

    const diffDays = Math.floor(diffHours / 24);
    return `Last updated ${diffDays} day${diffDays > 1 ? 's' : ''} ago.`;
  };

  const formatValue = (value: any, key: string): string => {
    if (value === null || value === undefined) return 'N/A';
    if (value === '') return 'N/A';

    // Handle numeric values
    if (typeof value === 'number') {
      // Temperature values
      if (key.includes('temperature') || key.includes('temp')) {
        return `${value.toFixed(2)}`;
      }
      // Voltage values
      if (key.includes('voltage')) {
        return `${value.toFixed(2)}`;
      }
      // Coordinate values
      if (key.includes('latitude') || key.includes('longitude')) {
        return value.toFixed(6);
      }
      // Default numeric formatting
      return value.toFixed(2);
    }

    return String(value);
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderDataSection = (title: string, fields: Array<{ key: string; label: string }>) => {
    if (!latestData?.data) return null;

    // Filter fields that have actual values (including 0)
    const fieldsWithData = fields.filter(field => {
      const value = latestData.data[field.key];
      return value !== null && value !== undefined && value !== '';
    });

    // If no fields have data, don't render the section
    if (fieldsWithData.length === 0) return null;

    return (
      <DataSection>
        <SectionTitle>{title}</SectionTitle>
        {fieldsWithData.map(field => {
          const value = latestData.data[field.key];

          return (
            <DataRow key={field.key}>
              <DataLabel>{field.label}</DataLabel>
              <DataValue>{formatValue(value, field.key)}</DataValue>
            </DataRow>
          );
        })}
      </DataSection>
    );
  };

  const isNetatmoDevice = () => {
    return device.deviceType?.startsWith('netatmo_');
  };

  const renderNetatmoData = () => {
    if (!latestData?.data) return null;

    return (
      <>
        {renderDataSection('Environmental', [
          { key: 'temperature', label: 'Temperature (°C)' },
          { key: 'humidity', label: 'Humidity (%)' },
          { key: 'pressure', label: 'Pressure (mbar)' },
          { key: 'absolute_pressure', label: 'Absolute Pressure (mbar)' },
          { key: 'co2', label: 'CO2 (ppm)' },
          { key: 'rain', label: 'Rain (mm)' },
          { key: 'sum_rain_1', label: 'Rain (Last Hour) (mm)' },
          { key: 'sum_rain_24', label: 'Rain (Last 24h) (mm)' },
          { key: 'min_temp', label: 'Min Temperature (°C)' },
          { key: 'max_temp', label: 'Max Temperature (°C)' }
        ])}

        {renderDataSection('Wind', [
          { key: 'wind_strength', label: 'Wind Strength (km/h)' },
          { key: 'wind_angle', label: 'Wind Angle (°)' },
          { key: 'gust_strength', label: 'Gust Strength (km/h)' },
          { key: 'gust_angle', label: 'Gust Angle (°)' }
        ])}

        {renderDataSection('Air Quality', [
          { key: 'noise', label: 'Noise Level (dB)' }
        ])}

        {renderDataSection('Device Info', [
          { key: 'battery_percent', label: 'Battery (%)' },
          { key: 'rf_status', label: 'RF Signal Strength' },
          { key: 'wifi_status', label: 'WiFi Signal Strength' }
        ])}
      </>
    );
  };

  const renderBuoyData = () => {
    if (!latestData?.data) return null;

    return (
      <>
        {renderDataSection('Wave Measurements', [
          { key: 'significant_wave_height', label: 'Significant Wave Height (Hs)' },
          { key: 'maximum_wave_height', label: 'Maximum Wave Height (Hmax)' },
          { key: 'peak_wave_period', label: 'Peak Wave Period (Tp)' },
          { key: 'mean_wave_period', label: 'Mean Wave Period (Tm)' },
          { key: 'peak_wave_direction', label: 'Peak Frequency Wave Direction (Dirp)' },
          { key: 'average_wave_height', label: 'Average Wave Height (Hav)' }
        ])}

        {renderDataSection('Environmental', [
          { key: 'temperature', label: 'Temperature (°c)' },
          { key: 'internal_sea_temperature', label: 'Sea Temperature (Potted Core) (ID 1) (HIGH RES)' },
          { key: 'external_sea_temperature', label: 'External Sea Temperature (ID 2) (HIGH RES)' },
          { key: 'spare_temperature', label: 'Spare Temperature (ID3) (HIGH RES)' },
          { key: 'board_temperature', label: 'AquaSense 100 Board Temperature (ID 4) (LOW RES)' },
          { key: 'housing_peripheral_edge_temperature', label: 'Solar Housing Peripheral Edge Temperature (ID 0) (LOW RES)' }
        ])}

        {renderDataSection('Location', [
          { key: 'latitude', label: 'GPS Latitude' },
          { key: 'longitude', label: 'GPS Longitude' }
        ])}

        {renderDataSection('Communication', [
          { key: 'signal_strength', label: 'Iridium Signal Strength' },
          { key: 'software_version', label: 'Software Version' }
        ])}

        {renderDataSection('Power System', [
          { key: 'rtc_battery_voltage', label: 'RTC Battery Voltage (V)' },
          { key: 'microchip_regulated_voltage', label: 'Processor Regulated Voltage (V)' },
          { key: 'solar_panel_voltage', label: 'APT Solar PCB Panel Voltage' },
          { key: 'solar_panel_current', label: 'APT Solar PCB Panel Current' },
          { key: 'battery_voltage', label: 'APT Solar PCB Battery Voltage' },
          { key: 'battery_current', label: 'APT Solar PCB Battery Current' },
          { key: 'battery_capacity', label: 'APT Solar PCB Battery Capacity' },
          { key: 'battery_fill', label: 'APT Solar PCB Battery Fill' },
          { key: 'battery_relative_fill', label: 'APT Solar PCB Battery Rel. Fill' },
          { key: 'battery_temperature', label: 'APT Solar PCB Battery Temperature' }
        ])}

        {renderDataSection('Water Quality', [
          { key: 'rdo_concentration', label: 'In-Situ RDO Oxygen Concentration' },
          { key: 'rdo_saturation', label: 'In-Situ RDO Oxygen Rel. Concentration' },
          { key: 'rdo_temperature', label: 'In-Situ RDO Temperature' }
        ])}

        {renderDataSection('Spectrum Analysis', [
          { key: 'spectrum_significant_wave_height', label: 'Spectrum - Significant Wave Height (Hs)' },
          { key: 'spectrum_maximum_wave_height', label: 'Spectrum - Maximum Wave Height (Hmax)' },
          { key: 'spectrum_peak_wave_period', label: 'Spectrum - Peak Wave Period (Tp)' },
          { key: 'spectrum_mean_wave_period', label: 'Spectrum - Mean Wave Period (Tav)' },
          { key: 'spectrum_peak_wave_direction', label: 'Spectrum - Peak Wave Direction (Dirp)' },
          { key: 'spectrum_average_wave_height', label: 'Spectrum - Average Wave Height' }
        ])}
      </>
    );
  };

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalPanel isOpen={isOpen}>
        <ModalHeader>
          <HeaderContent>
            <DeviceName>{device.name || `Device ${device.id}`}</DeviceName>
            {latestData && (
              <LastUpdate>{formatTimestamp(latestData.timestamp)}</LastUpdate>
            )}
          </HeaderContent>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        <ModalContent>
          {loading && <LoadingMessage>Loading latest data...</LoadingMessage>}

          {!loading && !latestData && (
            <NoDataMessage>No data available for this device</NoDataMessage>
          )}

          {!loading && latestData && (
            <>
              {isNetatmoDevice() ? renderNetatmoData() : renderBuoyData()}
            </>
          )}
        </ModalContent>

        <ModalFooter>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
        </ModalFooter>
      </ModalPanel>
    </ModalOverlay>
  );
};

export default LatestDataModal;
